import { clsx, type ClassValue } from 'clsx';
import memoize from 'lodash/memoize';
import { extendTailwindMerge } from 'tailwind-merge';

import { ThemeColor } from '../types/theme';

const customTwMerge = extendTailwindMerge({
  extend: {
    theme: {
      color: [
        // Dynamic colors
        'custom-color-darker',
        'custom-color',

        // Semantic colors
        'neutral',
        'primary-darker',
        'primary',
        'destructive-darker',
        'destructive',
        'failure-darker',
        'failure',
        'success-darker',
        'success',
        'warning-darker',
        'warning',

        // Basic colors
        'abs-white',
        'white',
        'space',
        'foreground',

        // Mnemonic colors
        'brown-darker',
        'brown',
        'magenta-darker',
        'magenta',
        'olive-darker',
        'olive',
        'purple-darker',
        'purple',
        'rust-darker',
        'rust',
        'slate-darker',
        'slate',
        'teal-darker',
        'teal',
      ],
      spacing: ['2xs', 'xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl'].flatMap(
        (size) =>
          [10, 30, 50, 150, 200].flatMap((n) => [
            `icon-${size}`,
            `icon-${size}-lh`,
            `${size}-${String(n).padStart(3, '0')}`,
          ]),
      ),
    },
    classGroups: {
      opacity: [
        'opacity-secondary',
        'opacity-tertiary',
        'opacity-disabled',
        'opacity-placeholder',
      ],
    },
  },
});

const organizeComponentIdentyingClassNames = memoize((classNames: string) => {
  if (!classNames.includes('@')) {
    return classNames;
  }

  const allClassNames = classNames.split(' ');

  const componentIdentifyingClassNames = allClassNames.filter((className) =>
    className.startsWith('@'),
  );

  const otherClassNames = allClassNames.filter(
    (className) => !className.startsWith('@'),
  );

  return [componentIdentifyingClassNames.at(-1), ...otherClassNames].join(' ');
});

export function cn(...inputs: ClassValue[]) {
  return organizeComponentIdentyingClassNames(customTwMerge(clsx(inputs)));
}

export const withColor = (color?: ThemeColor) =>
  color
    ? ({
        '--color-custom-color': `var(--color-${color})`,
        '--color-custom-color-darker': `var(--color-${color}-darker)`,
      } as React.CSSProperties)
    : {};
